import { useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { CalendarDays } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { MesReceitaStat } from "../chartUtils";
import { BAR_HOVER_PROPS, lineDots, TOOLTIP_CURSOR_BAND, barRowFromClick } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { payload: MesReceitaStat }[];
  label?:   string;
}
function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const mes = payload[0]?.payload;
  if (!mes) return null;
  const rows = [
    { name: "Preço Médio Geral",     color: "#8b5cf6", value: formatCurrency(mes.precoMedioGeral) },
    { name: "Preço Médio Pré-Chave", color: "#10b981", value: formatCurrency(mes.precoMedioPreChave) },
    { name: "Qtde Casas",            color: "#6366f1", value: formatNumber(mes.casas) },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[220px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {rows.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">{item.value}</span>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 mt-2">Clique para ver detalhes</p>
    </div>
  );
}

/* ─── Modal de detalhes ──────────────────────────────────────────────────── */
function ModalContent({ mes }: { mes: MesReceitaStat }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-purple-50 rounded-xl p-3 text-center">
          <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Total</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5">{formatCurrency(mes.total)}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center">
          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Quitado</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5">{formatCurrency(mes.pago)}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Pendente</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5">{formatCurrency(mes.aberto)}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left py-2 pr-3 text-slate-500 font-semibold">Cliente</th>
              <th className="text-left py-2 pr-3 text-slate-500 font-semibold">Tipo</th>
              <th className="text-right py-2 pr-3 text-slate-500 font-semibold">Valor</th>
              <th className="text-center py-2 text-slate-500 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {mes.items.map((item, i) => (
              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="py-1.5 pr-3 text-slate-700 max-w-[160px] truncate">{item.clientName || "—"}</td>
                <td className="py-1.5 pr-3 text-slate-500">{item.documentType || "—"}</td>
                <td className="py-1.5 pr-3 text-right font-medium text-slate-800">{formatCurrency(item.originalAmount)}</td>
                <td className="py-1.5 text-center">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${item.isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {item.isPaid ? "Pago" : "Aberto"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {mes.items.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-4">Sem registros</p>
        )}
      </div>
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:    MesReceitaStat[];
  loading: boolean;
}

export function ReceitasPorMes({ data, loading }: Props) {
  const [selected, setSelected] = useState<MesReceitaStat | null>(null);

  return (
    <>
      <ChartContainer
        title="Vendas por Mês"
        subtitle="Quitado e pendente por período, sem distratos até a data da pesquisa"
        loading={loading}
        empty={!loading && data.length === 0}
        height={300}
      >
        <ComposedChart data={data} margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />
          <Bar
            {...BAR_HOVER_PROPS}
            yAxisId="left"
            dataKey="pago"
            name="Quitado (R$)"
            fill="#10b981"
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
            cursor="pointer"
            onClick={(item: unknown) => {
              const d = barRowFromClick<MesReceitaStat>(item);
              if (d) setSelected(d);
            }}
          />
          <Bar
            {...BAR_HOVER_PROPS}
            yAxisId="left"
            dataKey="aberto"
            name="Pendente (R$)"
            fill="#f59e0b"
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
            cursor="pointer"
            onClick={(item: unknown) => {
              const d = barRowFromClick<MesReceitaStat>(item);
              if (d) setSelected(d);
            }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="total"
            name="Total (R$)"
            stroke="#8b5cf6"
            strokeWidth={2}
            {...lineDots("#8b5cf6")}
          />
        </ComposedChart>
      </ChartContainer>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center">
              <CalendarDays className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-sm font-semibold text-slate-800">{selected.mes}</span>
            <span className="text-xs text-slate-400">— {formatNumber(selected.items.length)} registro{selected.items.length !== 1 ? "s" : ""}</span>
          </div>
        )}
      >
        {selected && <ModalContent mes={selected} />}
      </Modal>
    </>
  );
}
